import { call, fork, take, put } from 'redux-saga/effects'
import { bindAsyncAction } from 'typescript-fsa-redux-saga'
import { Action } from 'typescript-fsa'
import i18n from 'i18next'
import { FacebookPageApi, ImprintApi, MediaFileApi } from '../../api/instances'

import { Severity, EventType } from '@professocial/professocialTypes/types/messageTypes'

import {
  getFacebookPage,
  getFacebookPageInitiated,
  getPotentialPageImprint,
  saveFacebookPageInformation,
  saveFacebookPageInformationInitiated,
  getPotentialPageImprintInitiated,
  getAllFacebookCategories,
  getAllFacebookCategoriesInitiated,
  uploadMediaFile,
  uploadMediaFileInitiated,
  updateCoverImage,
  updateCoverImageInitiated,
  updateProfileImage,
  updateProfileImageInitiated,
  listMediaFiles,
  listMediaFilesInitiated,
  uploadProfileMediaFile,
  uploadProfileMediaFileInitiated,
  uploadCoverMediaFileInitiated,
  uploadCoverMediaFile,
  listProfileImages,
  listProfileImagesInitiated,
  listCoverImages,
  listCoverImagesInitiated,
  setUpdateImprintAutomaticallyForCurrentUser,
  setUpdateImprintAutomaticallyForCurrentUserInitiated,
} from './facebook.actions'

import { requestFailed, requestFinished, requestStarted, setMessage } from '../common/common.actions'
import {
  GetAllFacebookCategoriesRequest,
  GetFacebookPageRequest,
  SaveFacebookPageInformationRequest, UpdateCoverImageRequest, UpdateProfileImageRequest,
} from '@3m5/sm_publish-frontend-api/apis/FacebookPageApi'
import {
  ListMediaFilesRequest,
  UploadMediaFileRequest,
  UploadTemporaryMediaFileRequest,
} from '@3m5/sm_publish-frontend-api'
import {
  SetUpdateImprintAutomaticallyForCurrentUserRequest as OriginalSetUpdateImprintAutomaticallyForCurrentUserRequest
} from '@professocialApi/professocial-core-frontend-api/apis/ImprintApi'

// Extended interface to include networkPageId
interface SetUpdateImprintAutomaticallyForCurrentUserRequest extends OriginalSetUpdateImprintAutomaticallyForCurrentUserRequest {
  networkPageId?: string;
}

// getFacebookPage
const getFacebookPageWorker = bindAsyncAction(getFacebookPage)(
  function * (params: GetFacebookPageRequest) {
    return yield call([FacebookPageApi, FacebookPageApi.getFacebookPage], params)
  },
)

function * watchGetFacebookPageInitiated () {
  while (true) {
    const action: Action<GetFacebookPageRequest> = yield take(getFacebookPageInitiated)
    yield put(requestStarted({ type: action.type }))
    try {
      yield getFacebookPageWorker(action.payload)
      yield put(requestFinished({ type: action.type }))
    } catch (e) {
      yield put(requestFailed({
        type: action.type,
        message: {
          severity: Severity.error,
          message: i18n.t('coreMessages:loadError', { context: i18n.t('facebookSettingsCommon:pageInfo') }),
          eventType: EventType.load,
        },
        error: e,
      }))
      console.error('error in watchGetFacebookPageInitiated: ', e)
    }
  }
}

// saveFacebookPageInformation
const saveFacebookPageInformationWorker = bindAsyncAction(saveFacebookPageInformation)(
  function * (params: SaveFacebookPageInformationRequest) {
    return yield call([FacebookPageApi, FacebookPageApi.saveFacebookPageInformation], params)
  },
)

function * watchSaveFacebookPageInformationInitiated () {
  while (true) {
    const action: Action<SaveFacebookPageInformationRequest> = yield take(saveFacebookPageInformationInitiated)
    yield put(requestStarted({
      type: action.type,
    }))
    try {
      yield saveFacebookPageInformationWorker(action.payload)
      yield put(requestFinished({
        type: action.type,
        message: {
          severity: Severity.success,
          message: i18n.t('coreMessages:areSuccessfullyUpdated', { context: i18n.t('facebookSettingsCommon:pageInfo') }),
          eventType: EventType.update,
        },
      }))
    } catch (e) {
      yield put(requestFailed({
        type: action.type,
        message: {
          severity: Severity.error,
          message: i18n.t('coreMessages:updatingError', { context: i18n.t('facebookSettingsCommon:pageInfo') }),
          eventType: EventType.update,
        },
        error: e,
      }))
      console.error('error in watchSaveFacebookPageInformationInitiated: ', e)
    }
  }
}

// getPotentialPageImprint
const getPotentialPageImprintWorker = bindAsyncAction(getPotentialPageImprint)(
  function * () {
    return yield call([ImprintApi, ImprintApi.getPotentialPageImprint])
  },
)

function * watchGetPotentialPageImprintInitiated () {
  while (true) {
    const action: Action<undefined> = yield take(getPotentialPageImprintInitiated)
    try {
      yield getPotentialPageImprintWorker(action.payload)
    } catch (e) {
      yield put(setMessage({
        severity: Severity.error,
        message: 'messages:getPotentialPageImprintError',
        eventType: EventType.update,
      }))
      console.error('error in watchGetPotentialPageImprintInitiated: ', e)
    }
  }
}

// getAllFacebookCategories
const getAllFacebookCategoriesWorker = bindAsyncAction(getAllFacebookCategories)(
  function * (params: GetAllFacebookCategoriesRequest) {
    return yield call([FacebookPageApi, FacebookPageApi.getAllFacebookCategories], params)
  },
)

function * watchGetAllFacebookCategoriesInitiated () {
  while (true) {
    const action: Action<GetAllFacebookCategoriesRequest> = yield take(getAllFacebookCategoriesInitiated)
    try {
      yield getAllFacebookCategoriesWorker(action.payload)
    } catch (e) {
      yield put(setMessage({
        severity: Severity.error,
        message: 'facebookSettingsMessages:getAllFacebookCategoriesError',
        eventType: EventType.update,
      }))
      console.error('error in watchGetAllFacebookCategoriesInitiated: ', e)
    }
  }
}

// uploadMediaFile
const uploadMediaFileWorker = bindAsyncAction(uploadMediaFile)(
  function * (params: UploadMediaFileRequest) {
    return yield call([MediaFileApi, MediaFileApi.uploadMediaFile], params)
  },
)

function * watchUploadMediaFileInitiated () {
  while (true) {
    const action: Action<UploadMediaFileRequest> = yield take(uploadMediaFileInitiated)
    try {
      yield uploadMediaFileWorker(action.payload)
    } catch (e) {
      yield put(setMessage({
        severity: Severity.error,
        message: 'messages:uploadMediaFileError',
        eventType: EventType.update,
      }))
      console.error('error in watchUploadMediaFileInitiated: ', e)
    }
  }
}

// updateCoverImage
const updateCoverImageWorker = bindAsyncAction(updateCoverImage)(
  function * (params: UpdateCoverImageRequest) {
    return yield call([FacebookPageApi, FacebookPageApi.updateCoverImage], params)
  },
)

function * watchUpdateCoverImageInitiated () {
  while (true) {
    const action: Action<UpdateCoverImageRequest> = yield take(updateCoverImageInitiated)
    yield put(requestStarted({
      type: action.type,
    }))
    try {
      yield updateCoverImageWorker(action.payload)
      yield put(requestFinished({
        type: action.type,
        message: {
          severity: Severity.success,
          message: i18n.t('coreMessages:isSuccessfullyUpdated', { context: i18n.t('facebookSettingsCommon:titleAreaImage') }),
          eventType: EventType.update,
        },
      }))
    } catch (e) {
      yield put(requestFailed({
        type: action.type,
        message: {
          severity: Severity.error,
          message: i18n.t('coreMessages:updateError', { context: i18n.t('facebookSettingsCommon:titleAreaImage') }),
          eventType: EventType.update,
        },
        error: e,
      }))
      console.error('error in watchUpdateCoverImageInitiated: ', e)
    }
  }
}

// updateCoverImage
const updateProfileImageWorker = bindAsyncAction(updateProfileImage)(
  function * (params: UpdateProfileImageRequest) {
    return yield call([FacebookPageApi, FacebookPageApi.updateProfileImage], params)
  },
)

function * watchUpdateProfileImageInitiated () {
  while (true) {
    const action: Action<UpdateProfileImageRequest> = yield take(updateProfileImageInitiated)
    yield put(requestStarted({
      type: action.type,
    }))
    try {
      yield updateProfileImageWorker(action.payload)
      yield put(requestFinished({
        type: action.type,
        message: {
          severity: Severity.success,
          message: i18n.t('coreMessages:isSuccessfullyUpdated', { context: i18n.t('facebookSettingsCommon:profileImage') }),
          eventType: EventType.update,
        },
      }))
    } catch (e) {
      yield put(requestFailed({
        type: action.type,
        message: {
          severity: Severity.error,
          message: i18n.t('coreMessages:updateError', { context: i18n.t('facebookSettingsCommon:profileImage') }),
          eventType: EventType.update,
        },
        error: e,
      }))
      console.error('error in watchUpdateProfileImageInitiated: ', e)
    }
  }
}

// listMediaFiles
const listMediaFilesWorker = bindAsyncAction(listMediaFiles)(
  function * (params: ListMediaFilesRequest) {
    return yield call([MediaFileApi, MediaFileApi.listMediaFiles], params)
  },
)

function * watchListMediaFilesInitiated () {
  while (true) {
    const action: Action<ListMediaFilesRequest> = yield take(listMediaFilesInitiated)
    try {
      yield listMediaFilesWorker(action.payload)
    } catch (e) {
      yield put(setMessage({
        severity: Severity.error,
        message: 'messages:listMediaFilesError',
        eventType: EventType.update,
      }))
      console.error('error in watchListMediaFilesInitiated: ', e)
    }
  }
}

// listCoverImages
const listCoverImagesWorker = bindAsyncAction(listCoverImages)(
  function * () {
    return yield call([MediaFileApi, MediaFileApi.listCoverImages])
  },
)

function * watchListCoverImagesInitiated () {
  while (true) {
    const action: Action<undefined> = yield take(listCoverImagesInitiated)
    try {
      yield listCoverImagesWorker(action.payload)
    } catch (e) {
      yield put(setMessage({
        severity: Severity.error,
        message: 'messages:listMediaFilesError',
        eventType: EventType.update,
      }))
      console.error('error in watchListMediaFilesInitiated: ', e)
    }
  }
}

// listProfileImages
const listProfileImagesWorker = bindAsyncAction(listProfileImages)(
  function * () {
    return yield call([MediaFileApi, MediaFileApi.listProfileImages])
  },
)

function * watchListProfileImagesInitiated () {
  while (true) {
    const action: Action<undefined> = yield take(listProfileImagesInitiated)
    try {
      yield listProfileImagesWorker(action.payload)
    } catch (e) {
      yield put(setMessage({
        severity: Severity.error,
        message: 'messages:listMediaFilesError',
        eventType: EventType.update,
      }))
      console.error('error in watchListMediaFilesInitiated: ', e)
    }
  }
}

// uploadProfileMediaFile
const uploadProfileMediaFileWorker = bindAsyncAction(uploadProfileMediaFile)(
  function * (params: UploadTemporaryMediaFileRequest) {
    return yield call([MediaFileApi, MediaFileApi.uploadTemporaryMediaFile], params)
  },
)

function * watchUploadProfileMediaFileInitiated () {
  while (true) {
    const action: Action<UploadTemporaryMediaFileRequest> = yield take(uploadProfileMediaFileInitiated)
    try {
      yield uploadProfileMediaFileWorker(action.payload)
    } catch (e) {
      yield put(setMessage({
        severity: Severity.error,
        message: 'messages:uploadProfileMediaFileError',
        eventType: EventType.update,
      }))
      console.error('error in watchUploadProfileMediaFileInitiated: ', e)
    }
  }
}

// uploadCoverMediaFile
const uploadCoverMediaFileWorker = bindAsyncAction(uploadCoverMediaFile)(
  function * (params: UploadTemporaryMediaFileRequest) {
    return yield call([MediaFileApi, MediaFileApi.uploadTemporaryMediaFile], params)
  },
)

function * watchUploadCoverMediaFileInitiated () {
  while (true) {
    const action: Action<UploadTemporaryMediaFileRequest> = yield take(uploadCoverMediaFileInitiated)
    try {
      yield uploadCoverMediaFileWorker(action.payload)
    } catch (e) {
      yield put(setMessage({
        severity: Severity.error,
        message: 'messages:uploadCoverMediaFileError',
        eventType: EventType.update,
      }))
      console.error('error in watchUploadCoverMediaFileInitiated: ', e)
    }
  }
}

// setUpdateImprintAutomaticallyForCurrentUser
const setUpdateImprintAutomaticallyForCurrentUserWorker = bindAsyncAction(setUpdateImprintAutomaticallyForCurrentUser)(
  function * (params: SetUpdateImprintAutomaticallyForCurrentUserRequest) {
    if (params && params.active !== undefined && params.networkPageId) {
      console.log('Setting updateImprintAutomatically with networkPageId:', params.networkPageId)
      return yield call(
        [FacebookPageApi, FacebookPageApi.setUpdateFacebookImprintAutomatically], 
        {
          networkPageId: params.networkPageId,
          updateImprintAutomatically: params.active
        }
      )
    } else {
      throw new Error('Missing required parameters: networkPageId or updateImprintAutomatically')
    }
  },
)

function * watchSetUpdateImprintAutomaticallyForCurrentUserInitiated () {
  while (true) {
    const action: Action<SetUpdateImprintAutomaticallyForCurrentUserRequest> = yield take(setUpdateImprintAutomaticallyForCurrentUserInitiated)
    yield put(requestStarted({
      type: action.type,
    }))
    try {
      yield setUpdateImprintAutomaticallyForCurrentUserWorker(action.payload)
      yield put(requestFinished({
        type: action.type,
        message: {
          severity: Severity.success,
          message: action.payload.active ? i18n.t('facebookSettingsCommon:syncEnabled') : i18n.t('facebookSettingsCommon:syncDisabled'),
          eventType: EventType.update,
        },
      }))
    } catch (e) {
      yield put(requestFailed({
        type: action.type,
        message: {
          severity: Severity.error,
          message: i18n.t('coreMessages:updateError', { context: i18n.t('facebookSettingsCommon:imprint') }),
          eventType: EventType.update,
        },
        error: e,
      }))
      console.error('error in watchSetUpdateImprintAutomaticallyForCurrentUserInitiated: ', e)
    }
  }
}

// getUpdateImprintAutomaticallyForCurrentUser saga removed as the flag now comes directly with the Facebook page data

export function * facebookSagas () {
  yield fork(watchGetFacebookPageInitiated)
  yield fork(watchSaveFacebookPageInformationInitiated)
  yield fork(watchGetAllFacebookCategoriesInitiated)
  yield fork(watchGetPotentialPageImprintInitiated)
  yield fork(watchUploadMediaFileInitiated)
  yield fork(watchListMediaFilesInitiated)
  yield fork(watchListCoverImagesInitiated)
  yield fork(watchListProfileImagesInitiated)
  yield fork(watchUpdateCoverImageInitiated)
  yield fork(watchUpdateProfileImageInitiated)
  yield fork(watchUploadProfileMediaFileInitiated)
  yield fork(watchUploadCoverMediaFileInitiated)
  yield fork(watchSetUpdateImprintAutomaticallyForCurrentUserInitiated)
}
